import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as BarCodeIcon } from 'src/Images/icons/common/barcode.svg';
import { ReactComponent as PrintIcon } from 'src/Images/icons/common/print.svg';
import { ReactComponent as PlusIcon } from 'src/Images/icons/common/plus.svg';
import { ReactComponent as UnHoldIcon } from 'src/Images/icons/common/unHoldIcon.svg';
import { ReactComponent as BreadcrumbIcon } from 'src/Images/icons/common/breadcrumbIcon.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { Modal, Alert } from 'react-bootstrap';
import PrintCameraWeightReport from 'src/components/WeightReport/PrintCameraWeightReport';
import InvoiceUploadModal from 'src/components/VehicleTrackingDetails/Invoice/InvoiceUploadModal';
import useHeaderSectionLogic from 'src/components/VehicleTrackingDetails/HeaderSection/useHeaderSectionLogic';
import Breadcrumb from 'src/components/Breadcrumb';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { useLocation, useParams } from 'react-router-dom';
import {
  updateVehicleTrackingOnHold,
  updateVehicleTrackingStatus,
  getReasonsDropdownOptionsForVehicleTracking,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { getVehicleTrackingDetail } from 'src/redux/Actions/VehicleTracking';
import { ReactComponent as ActiveIcon } from 'src/Images/icons/common/activeIcon.svg';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdownIcon.svg';
import { ReactComponent as CancelVehicleIcon } from 'src/Images/icons/common/cancelVehicle.svg';
import { ReactComponent as CloseIcon } from 'src/Images/icons/common/close.svg';

const CANCELLED_STATUS_ID = '5';

const STATUS_OPTIONS = [
  { id: '1', name: 'Registered' },
  { id: '2', name: 'Scheduled' },
  { id: '3', name: 'In Progress' },
  { id: '4', name: 'Completed' },
  { id: '5', name: 'Cancelled' },
];

const getStatusColor = (statusName) => {
  switch (statusName) {
    case 'Registered':
      return '#124387';
    case 'In Progress':
      return '#F59E0B';
    case 'Completed':
      return '#087F55';
    case 'Cancelled':
      return '#AC1D18';
    case 'Scheduled':
      return '#5B27A4';
    default:
      return '#6c757d';
  }
};

const INITIAL_CANCEL_STATUS_MODAL = {
  show: false,
  reason: null,
  reasonExtraData: '',
  reasonError: '',
  isSubmitting: false,
};

export default function HeaderSectionDesktop({
  toggleAddVehicleContentDrawer,
  openCreateLoadTab,
  toggleTargetVsActualForm,
  toggleGrnDrawer,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { vehicleTrackingNumber, workflowStageId } = useParams();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;
  const [showBreadcrumb, setShowBreadcrumb] = useState(false);
  const { reasonsDropdownOptionsForVehicleTracking } = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  // Tracks whether the resume vehicle tracking request is in progress.
  const [
    isVehicleTrackingResumeRequestInProgress,
    setIsVehicleTrackingResumeRequestInProgress,
  ] = useState(false);

  const cancelStatusDropdownRef = useRef(null);
  const [showCancelStatusDropdown, setShowCancelStatusDropdown] =
    useState(false);
  const [cancelStatusModal, setCancelStatusModal] = useState(
    INITIAL_CANCEL_STATUS_MODAL,
  );

  const {
    themeColor,
    vehicleNumber,
    vehicleTrackingCode,
    requestedStage,
    requestedStageEntry,
    requestedStageExit,
    isOnHold,
    showCameraWeightReport,
    setShowCameraWeightReport,
    showTargetVsActualAlert,
    setShowVehicleContentAlert,
    showVehicleContentAlert,
    setShowTargetVsActualAlert,
    showInvoiceUploadModal,
    handlePrint,
    handleSkipStage,
    handleMoveToNextStageWithoutTargetVsActualSubmit,
    navigateToReport,
    showVehicleDataAddButton,
    showCreateLoadButton,
    showResumeVehicleTrackingButton,
    isOrganisationAdmin,
    actionButtonAnimation,
    targetVsActualConfig,
    shouldRenderGrnComponent,
    shouldShowInvoiceUploadButton,
    toggleInvoiceUploadModal,
    showEnterExitButton,
    targetVsActualConfigLoaded,
    setShowEmptyLoadAlert,
    showEmptyLoadAlert,
    showEmptyManageLoadAlert,
    setShowEmptyManageLoadAlert,
    showIssueStampTableAlert,
    setShowIssueStampTableAlert,
    showCaptureWeightForOutwardAlert,
    setShowCaptureWeightForOutwardAlert,
    hasCreateVehicleTrackingEnterLogPermission,
    hasCreateVehicleTrackingExitLogPermission,
    hasCreateTargetVsActualPermission,
    direction,
    isVehicleVerified,
    vehicleTrackingStatus,
    hasUpdateVehicleTrackingCancelStatusPermission,
  } = useHeaderSectionLogic();

  useSetBreadcrumbData(location.pathname, vehicleTrackingCode);

  const showExitButton =
    hasCreateVehicleTrackingExitLogPermission &&
    requestedStageEntry &&
    !isOnHold;

  const showEntryButton =
    hasCreateVehicleTrackingEnterLogPermission && !isOnHold;

  const cancelStatusReasonOptions = [
    ...(reasonsDropdownOptionsForVehicleTracking?.map((reasonObj) => ({
      value: reasonObj.id,
      label: reasonObj.name,
    })) || []),
    { value: 0, label: 'Others' },
  ];

  const refreshVehicleTrackingDetail = () =>
    dispatch(
      getVehicleTrackingDetail(
        vehicleTrackingNumber,
        tenantOrganisationId,
        workflowStageId,
      ),
    );

  function handleResumeVehicleTracking() {
    if (isVehicleTrackingResumeRequestInProgress) return;
    setIsVehicleTrackingResumeRequestInProgress(true);
    dispatch(
      updateVehicleTrackingOnHold({
        tenant_organisation_id: tenantOrganisationId,
        vehicle_tracking_number: vehicleTrackingNumber,
        is_on_hold: false,
        on_hold_reason: null,
      }),
    )
      .then(refreshVehicleTrackingDetail)
      .finally(() => {
        setIsVehicleTrackingResumeRequestInProgress(false);
      });
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cancelStatusDropdownRef.current &&
        !cancelStatusDropdownRef.current.contains(event.target)
      ) {
        setShowCancelStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUpdateVehicleTrackingStatus = (statusId) => {
    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      vehicle_tracking_number: vehicleTrackingNumber,
      new_status: statusId,
    };

    dispatch(updateVehicleTrackingStatus(payload)).then(
      refreshVehicleTrackingDetail,
    );
  };

  const openCancelStatusModal = () => {
    setCancelStatusModal({ ...INITIAL_CANCEL_STATUS_MODAL, show: true });
    dispatch(
      getReasonsDropdownOptionsForVehicleTracking(tenantOrganisationId, 1),
    );
  };

  const closeCancelStatusModal = () => {
    if (cancelStatusModal.isSubmitting) return;
    setCancelStatusModal(INITIAL_CANCEL_STATUS_MODAL);
  };

  const isCancelStatusReasonInvalid = () => {
    if (!cancelStatusModal.reason) return true;
    if (
      cancelStatusModal.reason.value === 0 &&
      !cancelStatusModal.reasonExtraData.trim()
    ) {
      return true;
    }
    return false;
  };

  const handleConfirmCancelStatus = async () => {
    if (cancelStatusModal.isSubmitting) return;

    if (isCancelStatusReasonInvalid()) {
      setCancelStatusModal((prev) => ({
        ...prev,
        reasonError:
          prev.reason?.value === 0
            ? 'Please type the reason.'
            : 'Reason is required.',
      }));
      return;
    }

    setCancelStatusModal((prev) => ({
      ...prev,
      isSubmitting: true,
      reasonError: '',
    }));

    try {
      const isOthers = cancelStatusModal.reason.value === 0;
      await dispatch(
        updateVehicleTrackingStatus({
          tenant_organisation_id: tenantOrganisationId,
          vehicle_tracking_number: vehicleTrackingNumber,
          new_status: CANCELLED_STATUS_ID,
          reason_id: isOthers ? null : cancelStatusModal.reason.value,
          reason_extra_data: isOthers
            ? cancelStatusModal.reasonExtraData.trim()
            : null,
        }),
      );

      setCancelStatusModal(INITIAL_CANCEL_STATUS_MODAL);
      refreshVehicleTrackingDetail();
    } catch (error) {
      setCancelStatusModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const isVehicleTrackingCompleted = String(vehicleTrackingStatus?.id) === '4';

  const canChangeStatus =
    hasUpdateVehicleTrackingCancelStatusPermission &&
    !isVehicleTrackingCompleted;

  const canShowCancelStatusDropdown =
    canChangeStatus && showCancelStatusDropdown;

  return (
    <>
      <div className="header-vehicle-tracking-detail-page-padding">
        <div className="div-space-between header-content-vehicle-tracking-detail-page-padding">
          <div className="div-flex-row cg-10 div-align-center">
            <div
              className={`breadcrumb-container div-flex-row cg-5 div-align-center ${showBreadcrumb ? 'breadcrumb-open' : ''}`}
            >
              {showBreadcrumb && <Breadcrumb />}
              <div
                role="button"
                tabIndex={0}
                aria-label={
                  showBreadcrumb ? 'Collapse breadcrumb' : 'Expand breadcrumb'
                }
                className="footer-content-for-component div-align-center"
                onClick={() => setShowBreadcrumb((prev) => !prev)}
              >
                <BreadcrumbIcon
                  className={`icon-size-xsm ${showBreadcrumb ? 'rotate-icon-180' : ''}`}
                  color="#9F9F9F"
                />
              </div>
            </div>

            <div className="div-align-center text-xl text-semi-bold">
              {vehicleTrackingCode} / {vehicleNumber}
            </div>

            {isVehicleVerified && (
              <div className=" green">
                <ActiveIcon className="icon-size-xl div-align-center" />
              </div>
            )}
          </div>

          <div className="div-flex-row cg-10 div-align-center">
            <div className="position-relative" ref={cancelStatusDropdownRef}>
              <button
                type="button"
                disabled={!canChangeStatus}
                onClick={() => {
                  if (!canChangeStatus) return;
                  setShowCancelStatusDropdown((prev) => !prev);
                }}
                className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center ${
                  !canChangeStatus ? 'status-readonly' : ''
                }`}
                style={!canChangeStatus ? { cursor: 'default' } : undefined}
              >
                <div className="div-flex-row cg-10 div-align-center">
                  <span
                    className="vehicle-tracking-status-dot"
                    style={{
                      backgroundColor: getStatusColor(
                        vehicleTrackingStatus?.name,
                      ),
                    }}
                  />

                  <span className="text-sm text-medium-bold">
                    {vehicleTrackingStatus?.name || 'Status'}
                  </span>

                  {canChangeStatus && (
                    <DropdownIcon
                      className={`icon-size-10 ${
                        showCancelStatusDropdown ? 'rotate-icon-180' : ''
                      }`}
                      color={getColorCode(themeColor)}
                    />
                  )}
                </div>
              </button>

              {canShowCancelStatusDropdown && (
                <div className="status-dropdown-container">
                  {STATUS_OPTIONS.map((statusOption) => {
                    const isActive =
                      String(vehicleTrackingStatus?.id) === statusOption.id;
                    const isSelectable = [CANCELLED_STATUS_ID].includes(
                      statusOption.id,
                    );
                    const statusColor = isSelectable
                      ? getStatusColor(statusOption.name)
                      : '#9F9F9F';

                    return (
                      <div
                        key={statusOption.id}
                        role="button"
                        tabIndex={isSelectable ? 0 : -1}
                        aria-disabled={!isSelectable}
                        className={`status-dropdown-item cg-10 theme-text-color-${themeColor} text-sm-outfit text-medium-bold ${
                          isActive ? 'status-active' : ''
                        } ${!isSelectable ? 'status-disabled' : ''}`}
                        style={
                          isActive ? { '--status-color': statusColor } : {}
                        }
                        onClick={() => {
                          if (!isSelectable) return;
                          setShowCancelStatusDropdown(false);
                          if (isActive) return;
                          if (statusOption.id === CANCELLED_STATUS_ID) {
                            openCancelStatusModal();
                          } else {
                            handleUpdateVehicleTrackingStatus(statusOption.id);
                          }
                        }}
                      >
                        <span
                          className="vehicle-tracking-status-dot"
                          style={{ backgroundColor: statusColor }}
                        />
                        <span className="text-sm text-medium-bold">
                          {statusOption.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {showResumeVehicleTrackingButton && (
              <button
                className={`vehicle-tracking-detail-unhold-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                onClick={handleResumeVehicleTracking}
                disabled={isVehicleTrackingResumeRequestInProgress}
              >
                <span className="text-sm text-semi-bold right-padding border-right">
                  {isVehicleTrackingResumeRequestInProgress
                    ? 'Removing Hold...'
                    : 'Resume Vehicle Tracking'}
                </span>
                <UnHoldIcon color={getColorCode(themeColor)} />{' '}
              </button>
            )}
            {requestedStage?.show_vendor_details && (
              <>
                <BarCodeIcon
                  color={themeColor}
                  width={30}
                  onClick={handlePrint}
                />
                <PrintIcon
                  color={themeColor}
                  width={30}
                  onClick={() => setShowCameraWeightReport(true)}
                />
              </>
            )}
            {showVehicleDataAddButton && (
              <button
                className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                onClick={toggleAddVehicleContentDrawer}
              >
                <PlusIcon color={getColorCode(themeColor)} />
                <span className="text-sm text-semi-bold left-padding">
                  Vehicle Content
                </span>
              </button>
            )}
            {shouldShowInvoiceUploadButton() && (
              <button
                className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                onClick={toggleInvoiceUploadModal}
              >
                <PlusIcon color={getColorCode(themeColor)} />
                <span className="text-sm text-semi-bold left-padding">
                  Upload Invoice
                </span>
              </button>
            )}
            {showCreateLoadButton && !isOnHold && (
              <button
                className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                onClick={openCreateLoadTab}
              >
                <PlusIcon color={getColorCode(themeColor)} />
                <span className="text-sm text-semi-bold left-padding">
                  Create Load
                </span>
              </button>
            )}
            {shouldRenderGrnComponent() && !isOnHold && (
              <button
                className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                onClick={() => toggleGrnDrawer(true)}
              >
                <span className="text-sm">Create GRN</span>
              </button>
            )}

            {requestedStage?.completed && !isOnHold && direction === 1 && (
              <button
                className={`vehicle-tracking-detail-move-stage-btn theme-background-${themeColor} ${actionButtonAnimation ? 'btn-black-animation' : ''} div-align-center`}
                onClick={navigateToReport}
              >
                View Report
              </button>
            )}

            {hasCreateTargetVsActualPermission &&
              requestedStageEntry &&
              !requestedStage?.completed &&
              !isOnHold &&
              targetVsActualConfig.map((config) => (
                <button
                  key={config.id}
                  className={`vehicle-tracking-detail-move-stage-btn theme-background-${themeColor} div-align-center`}
                  onClick={() => toggleTargetVsActualForm(config)}
                >
                  <span className="text-sm">{config.title}</span>
                </button>
              ))}

            {showEnterExitButton() && (
              <>
                {showExitButton && (
                  <button
                    className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor} ${
                      actionButtonAnimation ? 'btn-black-animation' : ''
                    }`}
                    onClick={() =>
                      handleMoveToNextStageWithoutTargetVsActualSubmit()
                    }
                    disabled={!targetVsActualConfigLoaded}
                  >
                    <span className="text-sm">
                      Exit {requestedStage?.workflow_stage_name}
                    </span>
                  </button>
                )}
                {!requestedStageEntry && !requestedStageExit && (
                  <>
                    {isOrganisationAdmin && !isOnHold && (
                      <button
                        className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                        onClick={handleSkipStage}
                      >
                        <span className="text-sm">Skip</span>
                      </button>
                    )}
                    {showEntryButton && (
                      <button
                        className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor} ${
                          actionButtonAnimation ? 'btn-black-animation' : ''
                        }`}
                        onClick={() =>
                          handleMoveToNextStageWithoutTargetVsActualSubmit()
                        }
                      >
                        <span className="text-sm">
                          Enter {requestedStage?.workflow_stage_name}
                        </span>
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showCameraWeightReport}
        backdrop="static"
        keyboard={false}
        centered
        size="xl"
      >
        <div className="div-flex-column">
          <div className="div-flex-column rg-10">
            <div id="quotation-content">
              <PrintCameraWeightReport
                setShowCameraWeightReport={setShowCameraWeightReport}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        show={showTargetVsActualAlert}
        onHide={() => setShowTargetVsActualAlert(false)}
      >
        <Alert severity="info" className="m-0">
          Please enter the Target vs Actual data to exit the stage.
        </Alert>
      </Modal>

      <Modal
        show={showVehicleContentAlert}
        onHide={() => setShowVehicleContentAlert(false)}
      >
        <Alert severity="info" className="m-0">
          Please add vehicle content to exit the stage.
        </Alert>
      </Modal>

      <Modal
        show={showEmptyLoadAlert}
        onHide={() => setShowEmptyLoadAlert(false)}
      >
        <Alert severity="info" className="m-0">
          Please create load to exit the stage.
        </Alert>
      </Modal>

      <Modal
        show={showEmptyManageLoadAlert}
        onHide={() => setShowEmptyManageLoadAlert(false)}
      >
        <Alert severity="info" className="m-0">
          Please manage all load to exit the stage.
        </Alert>
      </Modal>

      <Modal
        show={showIssueStampTableAlert}
        onHide={() => setShowIssueStampTableAlert(false)}
      >
        <Alert severity="info" className="m-0">
          Please complete issue stamp table to exit the stage.
        </Alert>
      </Modal>

      <Modal
        show={showCaptureWeightForOutwardAlert}
        onHide={() => setShowCaptureWeightForOutwardAlert(false)}
      >
        <Alert severity="info" className="m-0">
          Please capture weight for outward loads to exit the stage.
        </Alert>
      </Modal>

      <Modal
        show={cancelStatusModal.show}
        onHide={closeCancelStatusModal}
        centered
      >
        <Modal.Header className="div-space-between div-align-center">
          <Modal.Title className="text-sm text-semi-bold">
            Reason For Cancellation
          </Modal.Title>
          <button
            type="button"
            className="icon-btn-none div-align-center"
            onClick={closeCancelStatusModal}
            disabled={cancelStatusModal.isSubmitting}
            aria-label="Close"
          >
            <CloseIcon className="icon-size-sm" color="#9F9F9F" />
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="div-flex-column rg-10">
            <div
              className="div-flex-center"
              style={{
                padding: '2px 0',
              }}
            >
              <CancelVehicleIcon width={600} height="auto" />
            </div>

            <div className="div-flex-column rg-5">
              <span className="text-sm text-semi-bold">Reason *</span>
              <div className="input-wrapper">
                <FilterMultiSelect
                  isMulti={false}
                  name="cancelStatusReason"
                  className={`multi-select-input-section ${
                    cancelStatusModal.reasonError ? 'form-input-error' : ''
                  }`}
                  classNamePrefix="react-select"
                  placeholder="Select Reason"
                  options={cancelStatusReasonOptions}
                  value={cancelStatusModal.reason}
                  onChange={(selectedOption) =>
                    setCancelStatusModal((prev) => ({
                      ...prev,
                      reason: selectedOption || null,
                      reasonExtraData: '',
                      reasonError: '',
                    }))
                  }
                />
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-10"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>

              {cancelStatusModal.reason?.value === 0 && (
                <input
                  name="reasonExtraData"
                  className="form-input-section"
                  type="text"
                  placeholder="Type Reason"
                  value={cancelStatusModal.reasonExtraData}
                  onChange={(e) =>
                    setCancelStatusModal((prev) => ({
                      ...prev,
                      reasonExtraData: e.target.value,
                      reasonError: '',
                    }))
                  }
                />
              )}

              {cancelStatusModal.reasonError && (
                <span className="text-xs" style={{ color: 'red' }}>
                  {cancelStatusModal.reasonError}
                </span>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className={`common-btn-outlined border-${themeColor} text-sm`}
            onClick={closeCancelStatusModal}
            disabled={cancelStatusModal.isSubmitting}
          >
            Back
          </button>
          <button
            type="button"
            className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
            onClick={handleConfirmCancelStatus}
            disabled={cancelStatusModal.isSubmitting}
          >
            <span className="text-sm">
              {cancelStatusModal.isSubmitting
                ? 'Cancelling...'
                : 'Confirm Cancel'}
            </span>
          </button>
        </Modal.Footer>
      </Modal>

      <InvoiceUploadModal
        show={showInvoiceUploadModal}
        onHide={toggleInvoiceUploadModal}
      />
    </>
  );
}
